import { useChatmessageModel } from '@/entities/chatmessage';
import { fileTob64 } from '@/shared';
import { defineStore, storeToRefs } from 'pinia';
import { useGlobalToast as useToast } from '@/shared/lib/toast';
import { computed, ref, watch } from 'vue';

export const useModel = defineStore('chatmessage-input-model', () => {
  const chatmessageModel = useChatmessageModel();
  const toast = useToast();

  /**
   * Текущий выбранный тред
   */
  const { threadId } = storeToRefs(chatmessageModel);

  /**
   * Тест сообщения
   */
  const message = ref<string | null>(null);

  /**
   * Файл
   */
  const file = ref<File | null>(null);

  /**
   * Индикатор создания/отправки сообщения
   */
  const createMessagePending = ref<boolean>(false);

  /**
   * Причина запрета на создания сообщения
   */
  const createMessageDisabledReason = computed(() => {
    if (!threadId.value) {
      return 'Не выбран чат с торговой точкой';
    }

    if (!message.value?.trim() && !file.value) {
      return 'Поле с сообщением пустое';
    }

    return null;
  });

  /**
   * Индикатор запрета на создание сообщения
   */
  const createMessageDisabled = computed(() => Boolean(createMessageDisabledReason.value));

  watch(threadId, () => {
    // При изменении треда нужно очищать значения в этих полях
    message.value = null;
    file.value = null;
  });

  /**
   * Создание сообщения
   */
  async function createMessage() {
    // Возвращаем return, если есть причина по которой нельзя создавать сообщение
    if (createMessageDisabled.value || !threadId.value) {
      toast.add({
        severity: 'warn',
        summary: 'Добавление сообщения',
        detail: createMessageDisabledReason.value
      });
      return;
    }

    if (createMessagePending.value) {
      return;
    }

    createMessagePending.value = true;
    const fileInBase64 = file.value ? await fileTob64(file.value) : undefined;

    const res = await chatmessageModel.updateMessage.post({
      // Сообщение всегда отправляется в текущий выбранный тред
      salespoint_ids: [threadId.value],
      message: (message.value?.trim() || undefined) as string,
      file: fileInBase64
    });

    if (res?._code === 0) {
      // При удачном вызове очищаем эти поля
      message.value = null;
      file.value = null;
    } else {
      toast.add({ severity: 'error', summary: 'Добавление сообщения', detail: res?._message });
    }

    createMessagePending.value = false;
    return res;
  }

  return {
    message,
    file,
    createMessagePending,
    createMessageDisabledReason,
    createMessageDisabled,
    createMessage
  };
});
